import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import {
  calculatePressureManometerResults,
  closePressureManometerSession,
  DEFAULT_VALIDITY_MONTHS,
  addPressureValidityDaysToIsoDate,
  computePressurePointMetrics,
  computeTemperatureCorrectionFactor,
  createPressureManometerSession,
  getPressureManometerSession,
  listPressureManometerPoints,
  listPressureManometerResults,
  listPressureManometerSessionsByInstrument,
  savePressureManometerPoints,
  updatePressureManometerSession,
  getTodayDailyEnvironmentLog,
  upsertTodayDailyEnvironmentLog,
} from '../../utils/api-pressure-manometer-pilot';
import {
  arePatternParamsEmpty,
  buildLinearSetpoints,
  resolvePressureTemplateProfile,
  resolvePressureTemplateProfileByCode,
  toFiniteNumber,
} from '../../utils/pressure-pilot-profiles';
import PressureCalibrationSimulator from './PressureCalibrationSimulator';

const EDITABLE_COLS = [
  'setpoint',
  's1_asc_pattern',
  's1_asc_instrument',
  's1_desc_pattern',
  's1_desc_instrument',
  's2_asc_pattern',
  's2_asc_instrument',
  's2_desc_pattern',
  's2_desc_instrument',
];

const DEFAULT_POINTS = [1, 2, 3, 4, 5, 6].map((n) => ({
  point_no: n,
  setpoint: '',
  unit: 'bar',
  s1_asc_pattern: '',
  s1_asc_instrument: '',
  s1_desc_pattern: '',
  s1_desc_instrument: '',
  s2_asc_pattern: '',
  s2_asc_instrument: '',
  s2_desc_pattern: '',
  s2_desc_instrument: '',
}));

function toGridRows(points = [], defaultUnit = 'bar') {
  const byPoint = new Map();
  points.forEach((p) => {
    const key = Number(p.point_no);
    const current =
      byPoint.get(key) || {
        point_no: key,
        setpoint: p.setpoint ?? '',
        unit: p.unit || defaultUnit,
        s1_asc_pattern: '',
        s1_asc_instrument: '',
        s1_desc_pattern: '',
        s1_desc_instrument: '',
        s2_asc_pattern: '',
        s2_asc_instrument: '',
        s2_desc_pattern: '',
        s2_desc_instrument: '',
      };

    if (p.series_no === 1 && p.direction === 'ASC') {
      current.s1_asc_pattern = p.pattern_reading ?? '';
      current.s1_asc_instrument = p.instrument_reading ?? '';
    }
    if (p.series_no === 1 && p.direction === 'DESC') {
      current.s1_desc_pattern = p.pattern_reading ?? '';
      current.s1_desc_instrument = p.instrument_reading ?? '';
    }
    if (p.series_no === 2 && p.direction === 'ASC') {
      current.s2_asc_pattern = p.pattern_reading ?? '';
      current.s2_asc_instrument = p.instrument_reading ?? '';
    }
    if (p.series_no === 2 && p.direction === 'DESC') {
      current.s2_desc_pattern = p.pattern_reading ?? '';
      current.s2_desc_instrument = p.instrument_reading ?? '';
    }

    byPoint.set(key, current);
  });

  return Array.from(byPoint.values()).sort((a, b) => a.point_no - b.point_no);
}

function buildRowsFromSetpoints(setpoints = [], rowCount = 6, unit = 'bar') {
  const total = Math.max(rowCount, setpoints.length || 0, 6);
  return Array.from({ length: total }, (_, idx) => ({
    point_no: idx + 1,
    setpoint: Number.isFinite(Number(setpoints[idx])) ? Number(setpoints[idx]) : '',
    unit,
    s1_asc_pattern: '',
    s1_asc_instrument: '',
    s1_desc_pattern: '',
    s1_desc_instrument: '',
    s2_asc_pattern: '',
    s2_asc_instrument: '',
    s2_desc_pattern: '',
    s2_desc_instrument: '',
  }));
}

function asNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function toNullableNumber(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function inferClassByDivision(rangeMin, rangeMax, division) {
  const fs = Math.abs(asNum(rangeMax, 0) - asNum(rangeMin, 0));
  const div = Math.abs(asNum(division, 0));
  if (!Number.isFinite(fs) || fs <= 0 || !Number.isFinite(div) || div <= 0) return null;
  return (div / fs) * 100;
}
const SQRT3 = Math.sqrt(3);

function computeLiveResults(rows = [], session = null) {
  const grouped = new Map();
  (rows || []).forEach((row) => {
    const pointNo = Number(row?.point_no);
    if (!Number.isFinite(pointNo)) return;
    const arr = grouped.get(pointNo) || [];
    arr.push(row);
    grouped.set(pointNo, arr);
  });

  return Array.from(grouped.entries())
    .map(([pointNo, pointRows]) => {
      const metrics = computePressurePointMetrics(pointRows, session || {});
      if (!metrics) return null;
      return {
        point_no: pointNo,
        ...metrics,
      };
    })
    .filter(Boolean)
    .sort((a, b) => asNum(a.point_no) - asNum(b.point_no));
}
function buildDeviationSeries(results = []) {
  const sorted = [...results].sort((a, b) => asNum(a.point_no) - asNum(b.point_no));
  return sorted.map((r, idx) => {
    const p = asNum(r.pattern_mean, 0);
    const errorPct = asNum(r.error_pct, 0);
    const uReportedPct = p === 0 ? 0 : (Math.abs(asNum(r.u_reported, 0)) / Math.abs(p)) * 100;
    return {
      xLabel: String(r.point_no ?? idx + 1),
      errorPct,
      uReportedPct,
    };
  });
}

const VALIDITY_OPTIONS = [
  { value: 6, label: '6 meses' },
  { value: 12, label: '1 año' },
  { value: 24, label: '2 años' },
];

function toIsoDate(value = new Date()) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function formatEsDate(isoDate) {
  const base = String(isoDate || '').slice(0, 10);
  if (!base) return '—';
  const d = new Date(`${base}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-AR');
}

export default function PressureManometerPilotPage() {
  const { instrumentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSessionId = searchParams.get('sessionId') || '';

  const inputRefs = useRef({});

  // Copiar/pegar columnas DENTRO de la grilla (no desde Excel): se seleccionan
  // columnas por el encabezado, se copian a un buffer y se pegan en otras.
  const [selectedCols, setSelectedCols] = useState(() => new Set());
  const [copiedBuffer, setCopiedBuffer] = useState(null); // [{ col, values: [...] }]

  const [instrument, setInstrument] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [session, setSession] = useState(null);

  const [rows, setRows] = useState(DEFAULT_POINTS);
  const [results, setResults] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [closing, setClosing] = useState(false);
  const [runningWorkflow, setRunningWorkflow] = useState(false);
  const [uiNotice, setUiNotice] = useState(null);
  const [savingDailyEnv, setSavingDailyEnv] = useState(false);
  const [dailyEnv, setDailyEnv] = useState({
    pressure_hpa: 0,
    temp_c: 0,
    humidity_rh: 0,
    pressure_unc_hpa: 1,
    temp_unc_c: 1,
    humidity_unc_rh: 3,
    notes: "",
  });

  const showNotice = (message, type = 'info') => {
    setUiNotice({ message, type });
  };
  const closeNotice = () => setUiNotice(null);

  const normalizeDailyEnv = (src = null) => ({
    pressure_hpa: Number(src?.pressure_hpa ?? 0),
    temp_c: Number(src?.temp_c ?? 0),
    humidity_rh: Number(src?.humidity_rh ?? 0),
    pressure_unc_hpa: Number(src?.pressure_unc_hpa ?? 1),
    temp_unc_c: Number(src?.temp_unc_c ?? 1),
    humidity_unc_rh: Number(src?.humidity_unc_rh ?? 3),
    notes: src?.notes || "",
  });

  const unit = useMemo(() => session?.instrument_unit || instrument?.unit || 'bar', [session, instrument]);
  const liveResults = useMemo(() => computeLiveResults(rows, session), [rows, session]);
  const displayedResults = useMemo(() => (liveResults.length ? liveResults : results), [liveResults, results]);
  const validityMonths = Number(session?.validity_months || DEFAULT_VALIDITY_MONTHS);
  const calibrationBaseDate = session?.calibration_date || session?.report_issue_date || toIsoDate();
  const validUntilPreview = addPressureValidityDaysToIsoDate(calibrationBaseDate, validityMonths);
  const derivedTempCorrectionFactor = useMemo(
    () =>
      computeTemperatureCorrectionFactor({
        ambientTemp: session?.ambient_temp_avg ?? session?.ambient_temp ?? dailyEnv?.temp_c,
        instrumentUrl: session?.instrument_url ?? instrument?.range_max ?? null,
      }),
    [session, instrument, dailyEnv]
  );
  const deviationSeries = useMemo(() => buildDeviationSeries(displayedResults), [displayedResults]);
  const templateProfile = useMemo(() => {
    const manualProfile = resolvePressureTemplateProfileByCode(session?.template_code);
    if (manualProfile) return manualProfile;

    return resolvePressureTemplateProfile({
      instrumentLrl: session?.instrument_lrl ?? instrument?.range_min ?? null,
      instrumentUrl: session?.instrument_url ?? instrument?.range_max ?? null,
    });
  }, [session, instrument]);
  const instrumentClassPct = useMemo(() => {
    const manual = toNullableNumber(session?.instrument_class);
    if (manual !== null && manual > 0) return manual;
    const fromTolerance = toNullableNumber(instrument?.tolerance);
    if (fromTolerance !== null && fromTolerance > 0) return fromTolerance;
    return null;
  }, [session, instrument]);
  const highErrorPointSet = useMemo(() => new Set(liveResults.filter((r) => Math.abs(asNum(r.error_pct, 0)) > 15).map((r) => asNum(r.point_no))), [liveResults]);
  const hasHighError = highErrorPointSet.size > 0;
  const outOfClassPointSet = useMemo(() => {
    if (!instrumentClassPct || instrumentClassPct <= 0) return new Set();
    return new Set(displayedResults.filter((r) => Math.abs(asNum(r.error_pct, 0)) > instrumentClassPct).map((r) => asNum(r.point_no)));
  }, [displayedResults, instrumentClassPct]);
  const hasOutOfClass = outOfClassPointSet.size > 0;

  async function loadInstrument() {
    const { data, error } = await supabase
      .from('instruments')
      .select('id, tag, lab_id, brand, model, serial, unit, range_min, range_max, resolution, client:client_id (name)')
      .eq('id', instrumentId)
      .single();
    if (error) throw error;
    setInstrument(data);
  }

  async function loadSessions() {
    const list = await listPressureManometerSessionsByInstrument(instrumentId);
    setSessions(list);
    if (!sessionId && !initialSessionId && list.length) setSessionId(list[0].id);
  }

  async function loadDailyEnvironment() {
    const log = await getTodayDailyEnvironmentLog();
    setDailyEnv(normalizeDailyEnv(log));
  }

  async function seedSessionDefaultsIfNeeded(rawSession) {
    if (!rawSession) return rawSession;

    const instrumentLrl = toFiniteNumber(rawSession.instrument_lrl, instrument?.range_min ?? 0);
    const instrumentUrl = toFiniteNumber(rawSession.instrument_url, instrument?.range_max ?? 0);
    const manualProfile = resolvePressureTemplateProfileByCode(rawSession.template_code);
    const activeProfile =
      manualProfile ||
      resolvePressureTemplateProfile({
        instrumentLrl,
        instrumentUrl,
      });

    if (!arePatternParamsEmpty(rawSession)) return rawSession;

    const defaults = {
      pattern_hysteresis: activeProfile.pattern_hysteresis,
      pattern_uncertainty_max: activeProfile.pattern_uncertainty_max,
      pattern_resolution: activeProfile.pattern_resolution,
      pattern_drift: activeProfile.pattern_drift,
      height_difference_m: activeProfile.height_difference_m,
    };

    const patched = await updatePressureManometerSession(rawSession.id, {
      template_code: activeProfile.templateCode,
      template_revision: activeProfile.templateRevision,
      pattern_hysteresis: defaults.pattern_hysteresis,
      pattern_uncertainty_max: defaults.pattern_uncertainty_max,
      pattern_resolution: defaults.pattern_resolution,
      pattern_drift: defaults.pattern_drift,
      height_difference_m: defaults.height_difference_m,
    });

    return patched;
  }

  function applySetpointDefaults(baseRows, activeSession) {
    if ((baseRows || []).some((r) => Number.isFinite(Number(r?.setpoint)))) return baseRows;

    const instrumentLrl = toFiniteNumber(activeSession?.instrument_lrl, instrument?.range_min ?? 0);
    const instrumentUrl = toFiniteNumber(activeSession?.instrument_url, instrument?.range_max ?? 0);
    const manualProfile = resolvePressureTemplateProfileByCode(activeSession?.template_code);
    const activeProfile =
      manualProfile ||
      resolvePressureTemplateProfile({
        instrumentLrl,
        instrumentUrl,
      });
    const profileDefaults = [...activeProfile.defaultSetpoints];
    const rangeMin = Math.min(instrumentLrl, instrumentUrl);
    const rangeMax = Math.max(instrumentLrl, instrumentUrl);
    const defaultsFitRange = profileDefaults.length > 0 && profileDefaults.every((value) => {
      const point = Number(value);
      return Number.isFinite(point) && point >= rangeMin && point <= rangeMax;
    });
    const defaults = defaultsFitRange
      ? profileDefaults
      : buildLinearSetpoints({ instrumentLrl, instrumentUrl, points: 6 });

    const rowsToUse = (baseRows?.length ? baseRows : buildRowsFromSetpoints(defaults, 6, activeSession?.instrument_unit || unit)).map(
      (row, idx) => ({
        ...row,
        point_no: idx + 1,
        setpoint: Number.isFinite(Number(defaults[idx])) ? defaults[idx] : row.setpoint,
        unit: activeSession?.instrument_unit || unit,
      })
    );

    return rowsToUse;
  }

  async function loadSessionData(id) {
    if (!id) {
      setSession(null);
      setRows(DEFAULT_POINTS.map((r) => ({ ...r, unit })));
      setResults([]);
      return;
    }

    const [s0, p, r] = await Promise.all([
      getPressureManometerSession(id),
      listPressureManometerPoints(id),
      listPressureManometerResults(id),
    ]);

    const s = await seedSessionDefaultsIfNeeded(s0);

    setSession(s);

    const grid = toGridRows(p, s.instrument_unit || unit);
    const seededGrid = applySetpointDefaults(
      grid.length ? grid : DEFAULT_POINTS.map((row) => ({ ...row, unit: s.instrument_unit || unit })),
      s
    );

    setRows(seededGrid);
    setResults(r || []);
  }

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadInstrument();
        await loadSessions();
        await loadDailyEnvironment();
      } catch (e) {
        console.error('[PressurePilot] load error', e);
      showNotice('No se pudo cargar el modulo de presion.', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [instrumentId]);

  useEffect(() => {
    loadSessionData(sessionId).catch((e) => {
      console.error('[PressurePilot] loadSessionData error', e);
      showNotice('No se pudo cargar la sesion seleccionada.', 'error');
    });
  }, [sessionId]);

  const updateRow = (idx, key, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };

  const focusNextExcelCell = (rowIndex, colKey) => {
    const colIndex = EDITABLE_COLS.indexOf(colKey);
    if (colIndex < 0) return;

    let nextRow = rowIndex + 1;
    let nextCol = colIndex;

    if (nextRow >= rows.length) {
      nextRow = 0;
      nextCol = colIndex + 1;
    }

    if (nextCol >= EDITABLE_COLS.length) return;

    const nextRef = inputRefs.current[`${nextRow}-${EDITABLE_COLS[nextCol]}`];
    if (nextRef && typeof nextRef.focus === 'function') {
      nextRef.focus();
      nextRef.select?.();
    }
  };

  // Pegar estilo Excel: copiás una columna o un bloque (de Excel o de acá) y,
  // al hacer Ctrl+V en una celda, se reparte desde esa celda hacia abajo y a la
  // derecha. Un handler por delegación en la tabla (cada input trae data-row/col).
  const handleGridPaste = (e) => {
    const el = e.target;
    if (!el || el.tagName !== 'INPUT' || el.dataset.row === undefined) return;
    const startRow = Number(el.dataset.row);
    const startCol = EDITABLE_COLS.indexOf(el.dataset.col);
    if (!Number.isInteger(startRow) || startCol < 0) return;

    const text = e.clipboardData?.getData('text');
    if (!text) return;
    // Filas por salto de línea, columnas por tabulación (formato del portapapeles de Excel).
    const matrix = text
      .replace(/\r\n?/g, '\n')
      .replace(/\n+$/, '')
      .split('\n')
      .map((line) => line.split('\t'));

    e.preventDefault();
    setRows((prev) => {
      const next = prev.map((r) => ({ ...r }));
      matrix.forEach((cols, r) => {
        const ri = startRow + r;
        if (ri < 0 || ri >= next.length) return; // no desborda la cantidad de puntos
        cols.forEach((raw, c) => {
          const ci = startCol + c;
          if (ci < 0 || ci >= EDITABLE_COLS.length) return;
          // Excel es-AR copia con coma decimal ("3,96") → normalizar a punto.
          next[ri][EDITABLE_COLS[ci]] = String(raw).trim().replace(',', '.');
        });
      });
      return next;
    });
  };

  // ── Copiar/pegar columnas dentro de la grilla ──────────────────────────────
  const orderedSelectedCols = () => EDITABLE_COLS.filter((c) => selectedCols.has(c));

  const toggleColumnSelect = (colKey) => {
    setSelectedCols((prev) => {
      const n = new Set(prev);
      if (n.has(colKey)) n.delete(colKey); else n.add(colKey);
      return n;
    });
  };

  const copySelectedColumns = () => {
    const cols = orderedSelectedCols();
    if (!cols.length) return;
    const buffer = cols.map((col) => ({ col, values: rows.map((r) => r[col] ?? '') }));
    setCopiedBuffer(buffer);
    setSelectedCols(new Set()); // limpiar: ahora se eligen los destinos
  };

  const pasteIntoSelectedColumns = () => {
    if (!copiedBuffer?.length) return;
    const targets = orderedSelectedCols();
    if (!targets.length) return;

    // Qué valores van a cada columna destino (igual que Excel):
    const plan = {}; // colKey -> values[]
    if (copiedBuffer.length === 1) {
      // 1 columna copiada → se replica en TODOS los destinos seleccionados.
      targets.forEach((t) => { plan[t] = copiedBuffer[0].values; });
    } else {
      // Varias columnas → se pegan en columnas consecutivas desde el destino más a la izquierda.
      const anchor = EDITABLE_COLS.indexOf(targets[0]);
      copiedBuffer.forEach((b, i) => {
        const destCol = EDITABLE_COLS[anchor + i];
        if (destCol) plan[destCol] = b.values;
      });
    }

    setRows((prev) => prev.map((row, ri) => {
      const nr = { ...row };
      Object.entries(plan).forEach(([col, values]) => { nr[col] = values[ri] ?? ''; });
      return nr;
    }));
    setSelectedCols(new Set());
  };

  const clearColumnSelection = () => setSelectedCols(new Set());

  const handleSaveDailyEnvironment = async () => {
    try {
      setSavingDailyEnv(true);
      const saved = await upsertTodayDailyEnvironmentLog(dailyEnv);
      setDailyEnv(normalizeDailyEnv(saved));
      if (sessionId && session) {
        const tempCorrectionFactor = computeTemperatureCorrectionFactor({
          ambientTemp: saved?.temp_c ?? 0,
          instrumentUrl: session?.instrument_url ?? instrument?.range_max ?? null,
        });
        const patch = {
          ambient_pressure_avg: Number(saved?.pressure_hpa ?? 0),
          ambient_temp_avg: Number(saved?.temp_c ?? 0),
          ambient_humidity_avg: Number(saved?.humidity_rh ?? 0),
          temp_correction_factor: tempCorrectionFactor,
        };
        await updatePressureManometerSession(sessionId, patch);
        setSession((s) => ({ ...s, ...patch }));
      }
      showNotice('Condiciones ambientales del dia guardadas.', 'success');
    } catch (e) {
      console.error('[PressurePilot] save daily env error', e);
      showNotice('No se pudieron guardar las condiciones del dia.', 'error');
    } finally {
      setSavingDailyEnv(false);
    }
  };

  const handleApplyDailyToCurrentSession = async () => {
    if (!sessionId || !session) {
      showNotice('Primero crea o selecciona una sesion.', 'info');
      return;
    }
    try {
      const tempCorrectionFactor = computeTemperatureCorrectionFactor({
        ambientTemp: dailyEnv?.temp_c ?? 0,
        instrumentUrl: session?.instrument_url ?? instrument?.range_max ?? null,
      });
      const patch = {
        ambient_pressure_avg: Number(dailyEnv?.pressure_hpa ?? 0),
        ambient_temp_avg: Number(dailyEnv?.temp_c ?? 0),
        ambient_humidity_avg: Number(dailyEnv?.humidity_rh ?? 0),
        temp_correction_factor: tempCorrectionFactor,
      };
      await updatePressureManometerSession(sessionId, patch);
      setSession((s) => ({ ...s, ...patch }));
      showNotice('Condiciones del dia aplicadas a la sesion actual.', 'success');
    } catch (e) {
      console.error('[PressurePilot] apply daily env error', e);
      showNotice('No se pudieron aplicar las condiciones del dia.', 'error');
    }
  };

  const syncInstrumentValidityWindow = async (baseDateIso, months) => {
    if (!instrumentId) return null;
    const nextIso = addPressureValidityDaysToIsoDate(baseDateIso, months);
    const { error } = await supabase
      .from('instruments')
      .update({
        lastcalibrationdate: baseDateIso,
        nextcalibrationdate: nextIso,
        calibrationintervalmonths: months,
      })
      .eq('id', instrumentId);
    if (error) throw error;
    return nextIso;
  };

  const handleCreateSession = async () => {
    try {
      const templateCode = searchParams.get('templateCode') || null;
      const created = await createPressureManometerSession({
        instrumentId,
        certificateType: 'NO_SAC',
        templateCode,
      });
      await loadSessions();
      setSessionId(created.id);
    } catch (e) {
      console.error('[PressurePilot] createSession error', e);
      showNotice('No se pudo crear la sesion de presion.', 'error');
    }
  };

  const updateInstrumentDueDate = async (baseDateIso, months) => {
    if (!instrumentId) return null;
    return syncInstrumentValidityWindow(baseDateIso, months);
  };


  const handleReplicateFromFirstSeries = () => {
    setRows((prev) => prev.map((row) => ({
      ...row,
      s1_desc_pattern: row.s1_asc_pattern,
      s1_desc_instrument: row.s1_asc_instrument,
      s2_asc_pattern: row.s1_asc_pattern,
      s2_asc_instrument: row.s1_asc_instrument,
      s2_desc_pattern: row.s1_asc_pattern,
      s2_desc_instrument: row.s1_asc_instrument,
    })));
  };

  const persistSessionParams = async () => {
    if (!sessionId || !session) return null;
    const baseDateIso = session.calibration_date || session.report_issue_date || toIsoDate();
    const months = Number(session.validity_months || DEFAULT_VALIDITY_MONTHS);
    const validUntil = addPressureValidityDaysToIsoDate(baseDateIso, months);
    const minDiv = toNullableNumber(session.instrument_resolution ?? instrument?.resolution);
    const tempCorrectionFactor = derivedTempCorrectionFactor;
    const updated = await updatePressureManometerSession(sessionId, {
      pattern_hysteresis: Number(session.pattern_hysteresis || 0),
      pattern_uncertainty_max: Number(session.pattern_uncertainty_max || 0),
      pattern_resolution: Number(session.pattern_resolution || 0),
      pattern_drift: Number(session.pattern_drift || 0),
      height_difference_m: Number(session.height_difference_m || 0),
      temp_correction_factor: tempCorrectionFactor,
      ambient_pressure_avg: Number(session.ambient_pressure_avg || 0),
      ambient_temp_avg: Number(session.ambient_temp_avg || 0),
      ambient_humidity_avg: Number(session.ambient_humidity_avg || 0),
      instrument_class: toNullableNumber(session.instrument_class),
      instrument_resolution: minDiv,
      validity_months: months,
      expiration_date: validUntil,   // columna correcta (la sesión de presión no tiene valid_until)
      notes: session.notes || '',
    });
    setSession(updated);
    setSession((s) => ({ ...s, validity_months: months, expiration_date: validUntil, instrument_resolution: minDiv, temp_correction_factor: tempCorrectionFactor }));
    return updated;
  };

  const handleSaveSessionParams = async () => {
    if (!sessionId || !session) return;
    try {
      await persistSessionParams();
      showNotice('Parametros y observaciones guardados.', 'success');
    } catch (e) {
      console.error(e);
      showNotice('No se pudieron guardar los parametros.', 'error');
    }
  };

  const handleSaveAllAndCalculate = async () => {
    if (!sessionId || !session) {
      showNotice('Primero crea o selecciona una sesion.', 'info');
      return;
    }

    try {
      setRunningWorkflow(true);
      await persistSessionParams();
      await savePressureManometerPoints(sessionId, rows.map((r) => ({ ...r, unit })));
      await updatePressureManometerSession(sessionId, { status: 'in_progress' });
      await calculatePressureManometerResults(sessionId);
      await loadSessionData(sessionId);
      showNotice('Guardado completo y calculo finalizado.', 'success');
    } catch (e) {
      console.error('[PressurePilot] save-all-and-calculate error', e);
      showNotice('No se pudo completar el flujo automatico.', 'error');
    } finally {
      setRunningWorkflow(false);
    }
  };

  const handleSavePoints = async () => {
    if (!sessionId) {
      showNotice('Primero crea o selecciona una sesion.', 'info');
      return;
    }
    try {
      setSaving(true);
      await savePressureManometerPoints(sessionId, rows.map((r) => ({ ...r, unit })));
      await updatePressureManometerSession(sessionId, { status: 'in_progress' });
      await loadSessionData(sessionId);
      showNotice('Lecturas guardadas.', 'success');
    } catch (e) {
      console.error('[PressurePilot] savePoints error', e);
      showNotice('No se pudieron guardar las lecturas.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCalculate = async () => {
    if (!sessionId) {
      showNotice('Primero crea o selecciona una sesion.', 'info');
      return;
    }

    try {
      setCalculating(true);
      await calculatePressureManometerResults(sessionId);
      await loadSessionData(sessionId);
      showNotice('Calculo completado.', 'success');
    } catch (e) {
      console.error('[PressurePilot] calculate error', e);
      showNotice('No se pudo calcular la sesion.', 'error');
    } finally {
      setCalculating(false);
    }
  };

  const handleClose = async () => {
    if (!sessionId) return;
    if (!confirm('Cerrar la sesion de manometro?')) return;

    try {
      setClosing(true);
      const months = Number(session?.validity_months || DEFAULT_VALIDITY_MONTHS);
      const baseDateIso = session?.calibration_date || session?.report_issue_date || toIsoDate();
      await updateInstrumentDueDate(baseDateIso, months);
      await closePressureManometerSession(sessionId);
      await loadSessionData(sessionId);
      await loadSessions();
      showNotice('Sesion cerrada.', 'success');
    } catch (e) {
      console.error('[PressurePilot] close error', e);
      showNotice('No se pudo cerrar la sesion.', 'error');
    } finally {
      setClosing(false);
    }
  };

  if (loading) return <div className="p-6">Cargando calibracion de presion...</div>;

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Calibracion de Manometros</h1>
          <p className="text-sm text-slate-200">
            Instrumento: <strong>{instrument?.tag || instrument?.id}</strong> · {instrument?.brand} {instrument?.model} · Cliente: {instrument?.client?.name || '-'}
          </p>
          <p className="text-xs text-slate-300 mt-1">
            Plantilla activa: <strong>{templateProfile.label}</strong>
          </p>
        </div>
        <button className="px-3 py-2 border rounded" onClick={() => navigate(`/instrument-details/${instrumentId}`)}>
          Volver a instrumento
        </button>
      </div>

      <div className="border rounded-lg p-4 space-y-4 bg-card">
        <div className="border border-slate-200 rounded-lg p-3 bg-white text-slate-900">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <div>
            <div className="text-sm font-semibold text-slate-900">Condiciones ambientales del dia (laboratorio)</div>
            <div className="text-xs text-slate-700">Se cargan una vez por la manana y se reutilizan para todas las calibraciones del dia.</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button className="px-3 py-2 border rounded" onClick={handleSaveDailyEnvironment} disabled={savingDailyEnv}>
                {savingDailyEnv ? 'Guardando dia...' : 'Guardar condiciones del dia'}
              </button>
              <button className="px-3 py-2 border rounded" onClick={handleApplyDailyToCurrentSession}>
                Aplicar a sesion actual
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 text-sm">
            <NumberField tone="light" label="Presión atmosférica (hPa)" value={dailyEnv.pressure_hpa} onChange={(v) => setDailyEnv((d) => ({ ...d, pressure_hpa: v }))} />
            <NumberField tone="light" label="Temperatura ambiente (°C)" value={dailyEnv.temp_c} onChange={(v) => setDailyEnv((d) => ({ ...d, temp_c: v }))} />
            <NumberField tone="light" label="Humedad relativa (%HR)" value={dailyEnv.humidity_rh} onChange={(v) => setDailyEnv((d) => ({ ...d, humidity_rh: v }))} />
            <NumberField tone="light" label="U presión (+/- hPa)" value={dailyEnv.pressure_unc_hpa} onChange={(v) => setDailyEnv((d) => ({ ...d, pressure_unc_hpa: v }))} />
            <NumberField tone="light" label="U temperatura (+/- °C)" value={dailyEnv.temp_unc_c} onChange={(v) => setDailyEnv((d) => ({ ...d, temp_unc_c: v }))} />
            <NumberField tone="light" label="U humedad (+/- %HR)" value={dailyEnv.humidity_unc_rh} onChange={(v) => setDailyEnv((d) => ({ ...d, humidity_unc_rh: v }))} />
          </div>
        </div>

        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="block text-xs text-slate-200 mb-1">Sesion de presion</label>
            <select
              className="border rounded px-3 py-2 min-w-[420px] bg-white text-slate-900"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
            >
              <option value="">Seleccionar...</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {new Date(s.created_at).toLocaleString()} · {s.status} · {s.certificate_type}
                </option>
              ))}
            </select>
          </div>
          <button className="px-3 py-2 bg-emerald-600 text-white rounded" onClick={handleCreateSession}>
            Nueva sesion de presion
          </button>
        </div>

        {session && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 text-sm">
              <Field label="Estado" value={session.status} />
              <Field label="Tipo cert." value={session.certificate_type} />
              <Field label="Unidad" value={session.instrument_unit || unit} />
              <NumberField
                label="Histeresis patron"
                value={session.pattern_hysteresis}
                readOnly
              />
              <NumberField
                label="U max patrón"
                value={session.pattern_uncertainty_max}
                readOnly
              />
              <NumberField
                label="Resolución patrón"
                value={session.pattern_resolution}
                readOnly
              />
              <NumberField
                label="Deriva patrón"
                value={session.pattern_drift}
                readOnly
              />
              <NumberField
                label="Delta altura (m)"
                value={session.height_difference_m}
                onChange={(v) => setSession((s) => ({ ...s, height_difference_m: v }))}
              />
              <NumberField
                label="Presion atmosferica (hPa)"
                value={session.ambient_pressure_avg}
                onChange={(v) => setSession((s) => ({ ...s, ambient_pressure_avg: v }))}
              />
              <NumberField
                label="Temperatura ambiente (C)"
                value={session.ambient_temp_avg}
                onChange={(v) => setSession((s) => ({ ...s, ambient_temp_avg: v }))}
              />
              <NumberField
                label="Humedad relativa (%HR)"
                value={session.ambient_humidity_avg}
                onChange={(v) => setSession((s) => ({ ...s, ambient_humidity_avg: v }))}
              />
              <NumberField
                label="Clase instrumento (%)"
                value={session.instrument_class}
                onChange={(v) => setSession((s) => ({ ...s, instrument_class: v }))}
              />
              <NumberField
                label="Menor division"
                value={session.instrument_resolution ?? instrument?.resolution ?? ''}
                onChange={(v) => setSession((s) => ({ ...s, instrument_resolution: v }))}
              />
              <div className="md:col-span-2 lg:col-span-2">
                <label className="block text-xs text-slate-200 mb-1">Validez del certificado</label>
                <select
                  className="w-full border rounded px-2 py-1 bg-white text-slate-900 border-slate-300"
                  value={String(validityMonths)}
                  onChange={(e) => setSession((s) => ({ ...s, validity_months: Number(e.target.value) }))}
                >
                  {VALIDITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-slate-300 mt-1">
                  Vence el: <span className="font-semibold text-slate-100">{formatEsDate(validUntilPreview)}</span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs text-slate-200 mb-1">Observaciones del técnico (aparecen en el certificado)</label>
              <textarea
                className="w-full min-h-[88px] border rounded px-3 py-2"
                placeholder="Ej: Instrumento con oscilacion leve en subida, se recomienda seguimiento en proxima calibracion."
                value={session.notes || ''}
                onChange={(e) => setSession((s) => ({ ...s, notes: e.target.value }))}
              />
            </div>
          </>
        )}

        {session && (
          <div className="flex gap-3 flex-wrap">
            <button className="px-3 py-2 border rounded" onClick={handleSaveSessionParams}>
              Guardar parametros
            </button>
            <button className="px-3 py-2 border rounded" onClick={handleReplicateFromFirstSeries}>
              Copiar S1 Asc a todas
            </button>
            <button className="px-3 py-2 border rounded" onClick={handleSavePoints} disabled={saving || runningWorkflow}>
              {saving ? 'Guardando lecturas...' : 'Guardar lecturas'}
            </button>
            <button className="px-3 py-2 bg-blue-700 text-white rounded" onClick={handleSaveAllAndCalculate} disabled={runningWorkflow}>
              {runningWorkflow ? 'Guardando y calculando...' : 'Guardar todo y calcular'}
            </button>
            <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={handleCalculate} disabled={calculating || runningWorkflow}>
              {calculating ? 'Calculando...' : 'Calcular resultados'}
            </button>
            <button className="px-3 py-2 bg-violet-600 text-white rounded" onClick={handleClose} disabled={closing || displayedResults.length === 0 || runningWorkflow}>
              {closing ? 'Cerrando...' : 'Cerrar sesion'}
            </button>
          </div>
        )}
        {/* El certificado lo emite el JEFE al aprobar (queda registrado y subido a la
            carpeta del cliente). Antes había un botón acá que generaba PDFs sueltos,
            sin registrar. Unificado con la plantilla eléctrica. */}
        {displayedResults.length > 0 && (
          <div className="mt-3 rounded border border-emerald-600/40 bg-emerald-600/10 px-3 py-2 text-sm text-emerald-900">
            ✓ Resultados calculados. El <b>jefe de laboratorio</b> los revisa y emite el certificado
            desde &ldquo;Certificados pendientes de aprobación&rdquo;.
          </div>
        )}
      </div>

      <PressureCalibrationSimulator onLoadWorksheet={setRows} />

      <div className="border rounded-lg p-4 bg-card overflow-auto">
        <h2 className="text-lg font-semibold mb-3">Datos obtenidos (2 series - Asc/Desc)</h2>
        <p className="text-xs text-slate-300 mb-2">Enter: baja a la siguiente fila y, al final, pasa a la siguiente columna. Ctrl+V: pegá una columna o bloque copiado de Excel desde la celda donde estés parado.</p>
        {/* Copiar/pegar columnas dentro de la grilla */}
        <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
          <span className="text-slate-400">
            Copiar columnas: clic en el <b className="text-slate-200">encabezado</b> para seleccionar (una o varias) → <b className="text-slate-200">Copiar</b> → seleccioná el/los destino(s) → <b className="text-slate-200">Pegar</b>.
          </span>
          <div className="ml-auto flex items-center gap-2">
            {copiedBuffer?.length > 0 && (
              <span className="text-emerald-300">{copiedBuffer.length} col. copiada{copiedBuffer.length > 1 ? 's' : ''}</span>
            )}
            <button
              onClick={copySelectedColumns}
              disabled={selectedCols.size === 0}
              className="px-3 py-1.5 rounded border border-white/20 text-slate-100 hover:bg-white/10 disabled:opacity-40"
            >
              Copiar{selectedCols.size > 0 ? ` (${selectedCols.size})` : ' columnas'}
            </button>
            <button
              onClick={pasteIntoSelectedColumns}
              disabled={!copiedBuffer?.length || selectedCols.size === 0}
              className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40"
            >
              Pegar{selectedCols.size > 0 ? ` en (${selectedCols.size})` : ' en selección'}
            </button>
            {selectedCols.size > 0 && (
              <button onClick={clearColumnSelection} className="px-2 py-1.5 rounded border border-white/15 text-slate-400 hover:text-white">
                Limpiar
              </button>
            )}
          </div>
        </div>
        {hasOutOfClass && (
          <div className="mb-3 rounded border border-amber-400 bg-amber-50 text-amber-900 px-3 py-2 text-sm">
            Hay puntos fuera de clase (|Error %| mayor a Clase %).
          </div>
        )}
        {hasHighError && (
          <div className="mb-3 rounded border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-sm">
            Alerta: se detectaron puntos con error mayor al 15%. Revisa lecturas ingresadas o estado del instrumento.
          </div>
        )}
        <table className="min-w-[1450px] text-sm border-collapse w-full" onPaste={handleGridPaste}>
          <thead>
            <tr className="bg-muted/50 text-center">
              <th
                rowSpan={3}
                onClick={() => toggleColumnSelect('setpoint')}
                title="Clic para seleccionar esta columna (copiar/pegar)"
                className={`border px-2 py-1 align-middle cursor-pointer select-none ${selectedCols.has('setpoint') ? 'bg-blue-200 text-blue-900 ring-2 ring-blue-500 ring-inset' : 'hover:bg-white/10'}`}
              >
                Punto de Calibracion ({unit})
              </th>
              <th className="border px-2 py-1" colSpan={4}>1era Serie</th>
              <th className="border px-2 py-1" colSpan={4}>2da Serie</th>
            </tr>
            <tr className="bg-muted/40 text-center">
              <th className="border px-2 py-1" colSpan={2}>Ascendente</th>
              <th className="border px-2 py-1" colSpan={2}>Descendente</th>
              <th className="border px-2 py-1" colSpan={2}>Ascendente</th>
              <th className="border px-2 py-1" colSpan={2}>Descendente</th>
            </tr>
            <tr className="bg-muted/30 text-center">
              {[
                { col: 's1_asc_pattern', label: 'Patron' },
                { col: 's1_asc_instrument', label: 'Instrumento' },
                { col: 's1_desc_pattern', label: 'Patron' },
                { col: 's1_desc_instrument', label: 'Instrumento' },
                { col: 's2_asc_pattern', label: 'Patron' },
                { col: 's2_asc_instrument', label: 'Instrumento' },
                { col: 's2_desc_pattern', label: 'Patron' },
                { col: 's2_desc_instrument', label: 'Instrumento' },
              ].map((h) => (
                <th
                  key={h.col}
                  onClick={() => toggleColumnSelect(h.col)}
                  title="Clic para seleccionar esta columna (copiar/pegar)"
                  className={`border px-2 py-1 cursor-pointer select-none ${selectedCols.has(h.col) ? 'bg-blue-200 text-blue-900 ring-2 ring-blue-500 ring-inset' : 'hover:bg-white/10'}`}
                >
                  {h.label} ({unit})
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.point_no} className={outOfClassPointSet.has(asNum(r.point_no)) ? 'bg-amber-50/80' : highErrorPointSet.has(asNum(r.point_no)) ? 'bg-red-50/70' : ''}>
                <CellInput
                  colKey="setpoint"
                  rowIndex={idx}
                  value={r.setpoint}
                  onChange={(v) => updateRow(idx, 'setpoint', v)}
                  onEnter={focusNextExcelCell}
                  inputRef={(el) => { inputRefs.current[`${idx}-setpoint`] = el; }}
                  danger={highErrorPointSet.has(asNum(r.point_no)) || outOfClassPointSet.has(asNum(r.point_no))}
                />
                <CellInput
                  colKey="s1_asc_pattern"
                  rowIndex={idx}
                  value={r.s1_asc_pattern}
                  onChange={(v) => updateRow(idx, 's1_asc_pattern', v)}
                  onEnter={focusNextExcelCell}
                  inputRef={(el) => { inputRefs.current[`${idx}-s1_asc_pattern`] = el; }}
                  danger={highErrorPointSet.has(asNum(r.point_no)) || outOfClassPointSet.has(asNum(r.point_no))}
                />
                <CellInput
                  colKey="s1_asc_instrument"
                  rowIndex={idx}
                  value={r.s1_asc_instrument}
                  onChange={(v) => updateRow(idx, 's1_asc_instrument', v)}
                  onEnter={focusNextExcelCell}
                  inputRef={(el) => { inputRefs.current[`${idx}-s1_asc_instrument`] = el; }}
                  danger={highErrorPointSet.has(asNum(r.point_no)) || outOfClassPointSet.has(asNum(r.point_no))}
                />
                <CellInput
                  colKey="s1_desc_pattern"
                  rowIndex={idx}
                  value={r.s1_desc_pattern}
                  onChange={(v) => updateRow(idx, 's1_desc_pattern', v)}
                  onEnter={focusNextExcelCell}
                  inputRef={(el) => { inputRefs.current[`${idx}-s1_desc_pattern`] = el; }}
                  danger={highErrorPointSet.has(asNum(r.point_no)) || outOfClassPointSet.has(asNum(r.point_no))}
                />
                <CellInput
                  colKey="s1_desc_instrument"
                  rowIndex={idx}
                  value={r.s1_desc_instrument}
                  onChange={(v) => updateRow(idx, 's1_desc_instrument', v)}
                  onEnter={focusNextExcelCell}
                  inputRef={(el) => { inputRefs.current[`${idx}-s1_desc_instrument`] = el; }}
                  danger={highErrorPointSet.has(asNum(r.point_no)) || outOfClassPointSet.has(asNum(r.point_no))}
                />
                <CellInput
                  colKey="s2_asc_pattern"
                  rowIndex={idx}
                  value={r.s2_asc_pattern}
                  onChange={(v) => updateRow(idx, 's2_asc_pattern', v)}
                  onEnter={focusNextExcelCell}
                  inputRef={(el) => { inputRefs.current[`${idx}-s2_asc_pattern`] = el; }}
                  danger={highErrorPointSet.has(asNum(r.point_no)) || outOfClassPointSet.has(asNum(r.point_no))}
                />
                <CellInput
                  colKey="s2_asc_instrument"
                  rowIndex={idx}
                  value={r.s2_asc_instrument}
                  onChange={(v) => updateRow(idx, 's2_asc_instrument', v)}
                  onEnter={focusNextExcelCell}
                  inputRef={(el) => { inputRefs.current[`${idx}-s2_asc_instrument`] = el; }}
                  danger={highErrorPointSet.has(asNum(r.point_no)) || outOfClassPointSet.has(asNum(r.point_no))}
                />
                <CellInput
                  colKey="s2_desc_pattern"
                  rowIndex={idx}
                  value={r.s2_desc_pattern}
                  onChange={(v) => updateRow(idx, 's2_desc_pattern', v)}
                  onEnter={focusNextExcelCell}
                  inputRef={(el) => { inputRefs.current[`${idx}-s2_desc_pattern`] = el; }}
                  danger={highErrorPointSet.has(asNum(r.point_no)) || outOfClassPointSet.has(asNum(r.point_no))}
                />
                <CellInput
                  colKey="s2_desc_instrument"
                  rowIndex={idx}
                  value={r.s2_desc_instrument}
                  onChange={(v) => updateRow(idx, 's2_desc_instrument', v)}
                  onEnter={focusNextExcelCell}
                  inputRef={(el) => { inputRefs.current[`${idx}-s2_desc_instrument`] = el; }}
                  danger={highErrorPointSet.has(asNum(r.point_no)) || outOfClassPointSet.has(asNum(r.point_no))}
                />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border rounded-lg p-4 bg-card overflow-auto">
        <h2 className="text-lg font-semibold mb-3">Resultados calculados</h2>
        {!displayedResults.length ? (
          <p className="text-sm text-slate-200">Sin resultados aun. Guarda lecturas y ejecuta calculo.</p>
        ) : (
          <>
            <table className="min-w-[900px] text-sm border-collapse w-full">
              <thead>
                <tr className="bg-muted/50">
                  <th className="border px-2 py-1">Pto</th>
                  <th className="border px-2 py-1">Media Patron</th>
                  <th className="border px-2 py-1">Media Instr.</th>
                  <th className="border px-2 py-1">Histeresis</th>
                  <th className="border px-2 py-1">Error Abs</th>
                  <th className="border px-2 py-1">Error %</th>
                  <th className="border px-2 py-1">Repet.</th>
                  <th className="border px-2 py-1">uc</th>
                  <th className="border px-2 py-1">U (k=2)</th>
                  <th className="border px-2 py-1">U reportada</th>
                </tr>
              </thead>
              <tbody>
                {displayedResults.map((r) => (
                  <tr key={r.point_no} className={outOfClassPointSet.has(asNum(r.point_no)) ? 'bg-amber-50/80' : highErrorPointSet.has(asNum(r.point_no)) ? 'bg-red-50/70' : ''}>
                    <td className="border px-2 py-1 text-center">{r.point_no}</td>
                    <td className="border px-2 py-1">{fmt(r.pattern_mean)}</td>
                    <td className="border px-2 py-1">{fmt(r.instrument_mean)}</td>
                    <td className="border px-2 py-1">{fmt(r.hysteresis)}</td>
                    <td className="border px-2 py-1">{fmt(r.error_abs)}</td>
                    <td className={`border px-2 py-1 ${Math.abs(asNum(r.error_pct, 0)) > 15 ? 'bg-red-100 text-red-800 font-semibold' : ''}`}>{fmt(r.error_pct, 4)} %</td>
                    <td className="border px-2 py-1">{fmt(r.repeatability)}</td>
                    <td className="border px-2 py-1">{fmt(r.uc)}</td>
                    <td className="border px-2 py-1">{fmt(r.u_expanded)}</td>
                    <td className="border px-2 py-1 font-semibold">{fmt(r.u_reported)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-4">
              <h3 className="text-sm font-semibold mb-2">Grafica de desvio (error % y U reportada %)</h3>
              <PressureDeviationChart points={deviationSeries} classPct={instrumentClassPct} />
            </div>
          </>
        )}
      </div>
      <StatusModal
        open={Boolean(uiNotice)}
        type={uiNotice?.type}
        message={uiNotice?.message}
        onClose={closeNotice}
      />
    </div>
  );
}

function fmt(v, d = 6) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return n.toFixed(d);
}

function CellInput({
  value,
  onChange,
  inputRef,
  rowIndex,
  colKey,
  onEnter,
  danger = false,
}) {
  return (
    <td className="border px-1 py-1">
      <input
        ref={inputRef}
        type="number"
        step="any"
        data-row={rowIndex}
        data-col={colKey}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onEnter?.(rowIndex, colKey);
          }
        }}
        className={`w-full border rounded px-2 py-1 ${danger ? 'border-red-400 bg-red-50 text-red-900' : ''}` }
      />
    </td>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-200">{label}</div>
      <div className="border rounded px-2 py-1 bg-background">{value ?? '-'}</div>
    </div>
  );
}

function NumberField({ label, value, onChange, tone = 'dark', readOnly = false }) {
  const labelClass = tone === 'light' ? 'text-slate-700' : 'text-slate-200';
  const inputClass =
    tone === 'light'
      ? `w-full border rounded px-2 py-1 bg-white text-slate-900 border-slate-300 ${readOnly ? 'bg-slate-100 text-slate-700 cursor-not-allowed' : ''}`
      : `w-full border rounded px-2 py-1 bg-white text-slate-900 ${readOnly ? 'bg-slate-100 text-slate-700 cursor-not-allowed' : ''}`;

  return (
    <div>
      <div className={`text-xs ${labelClass}`}>{label}</div>
      <input
        type="number"
        step="any"
        value={value ?? 0}
        readOnly={readOnly}
        onChange={readOnly ? undefined : (e) => onChange?.(e.target.value)}
        className={inputClass}
      />
    </div>
  );
}

function StatusModal({ open, type = 'info', message = '', onClose }) {
  if (!open) return null;
  const styleByType = {
    success: {
      border: 'border-emerald-200',
      bg: 'bg-emerald-50',
      text: 'text-emerald-900',
      badge: 'bg-emerald-600',
      label: 'Exito',
    },
    error: {
      border: 'border-red-200',
      bg: 'bg-red-50',
      text: 'text-red-900',
      badge: 'bg-red-600',
      label: 'Error',
    },
    info: {
      border: 'border-slate-200',
      bg: 'bg-slate-50',
      text: 'text-slate-900',
      badge: 'bg-slate-700',
      label: 'Info',
    },
  };
  const theme = styleByType[type] || styleByType.info;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/30 p-4">
      <div className={`w-full max-w-md rounded-xl border shadow-xl ${theme.border} ${theme.bg} ${theme.text}`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/10">
          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold text-white ${theme.badge}`}>
            {theme.label}
          </span>
          <button className="text-sm px-2 py-1 rounded border border-black/20 hover:bg-white/70" onClick={onClose}>
            Cerrar
          </button>
        </div>
        <div className="px-4 py-4 text-sm">{message}</div>
        <div className="px-4 pb-4 flex justify-end">
          <button className="px-3 py-2 rounded bg-slate-900 text-white text-sm hover:bg-slate-800" onClick={onClose}>
            Aceptar
          </button>
        </div>
      </div>
    </div>
  );
}
function PressureDeviationChart({ points = [], classPct = null }) {
  const width = 980;
  const height = 300;
  const left = 52;
  const right = 20;
  const top = 16;
  const bottom = 34;
  const w = width - left - right;
  const h = height - top - bottom;

  if (!points.length) {
    return <div className="text-sm text-slate-200">Sin datos para graficar.</div>;
  }

  const allY = points.flatMap((p) => [Math.abs(p.errorPct), Math.abs(p.uReportedPct)]).filter(Number.isFinite);
  const classBand = Math.abs(asNum(classPct, 0));
  const yMax = Math.max(1, ...allY, classBand) * 1.15;
  const yMin = -yMax;

  const xAt = (idx) => left + (idx * w) / Math.max(points.length - 1, 1);
  const yAt = (val) => top + ((yMax - asNum(val, 0)) / (yMax - yMin)) * h;

  const errorPath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(p.errorPct)}`)
    .join(' ');

  const uPath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(p.uReportedPct)}`)
    .join(' ');

  return (
    <div className="border rounded bg-background p-2 overflow-x-auto">
      <svg width={width} height={height} role="img" aria-label="Grafica de error e incertidumbre">
        <rect x={left} y={top} width={w} height={h} fill="white" stroke="#d1d5db" />

        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const y = top + h * f;
          const val = yMax - (yMax - yMin) * f;
          return (
            <g key={`g-${f}`}>
              <line x1={left} y1={y} x2={left + w} y2={y} stroke="#e5e7eb" />
              <text x={left - 8} y={y + 4} fontSize="11" textAnchor="end" fill="#6b7280">
                {val.toFixed(2)}
              </text>
            </g>
          );
        })}

        {classBand > 0 && (
          <>
            <line x1={left} y1={yAt(classBand)} x2={left + w} y2={yAt(classBand)} stroke="#f59e0b" strokeWidth="1.6" strokeDasharray="6 4" />
            <line x1={left} y1={yAt(-classBand)} x2={left + w} y2={yAt(-classBand)} stroke="#f59e0b" strokeWidth="1.6" strokeDasharray="6 4" />
          </>
        )}

        {points.map((p, i) => (
          <text key={`x-${p.xLabel}-${i}`} x={xAt(i)} y={height - 10} fontSize="11" textAnchor="middle" fill="#6b7280">
            {p.xLabel}
          </text>
        ))}

        <path d={errorPath} stroke="#dc2626" strokeWidth="2" fill="none" />
        <path d={uPath} stroke="#2563eb" strokeWidth="2" fill="none" />

        {points.map((p, i) => (
          <g key={`pt-${p.xLabel}-${i}`}>
            <circle cx={xAt(i)} cy={yAt(p.errorPct)} r="3" fill={classBand > 0 && Math.abs(asNum(p.errorPct, 0)) > classBand ? '#d97706' : '#dc2626'} />
            <circle cx={xAt(i)} cy={yAt(p.uReportedPct)} r="3" fill="#2563eb" />
          </g>
        ))}

        <g>
          <line x1={left + 8} y1={top + 10} x2={left + 28} y2={top + 10} stroke="#dc2626" strokeWidth="2" />
          <text x={left + 34} y={top + 14} fontSize="11" fill="#374151">Error %</text>
          <line x1={left + 92} y1={top + 10} x2={left + 112} y2={top + 10} stroke="#2563eb" strokeWidth="2" />
          <text x={left + 118} y={top + 14} fontSize="11" fill="#374151">U reportada %</text>
        </g>
      </svg>
    </div>
  );
}























